import { motion } from "framer-motion";
import { Zap, CreditCard, Bell, FileText, Wallet, ArrowUpRight, Check, Users, Flag } from "lucide-react";
import { useI18n } from "../i18n/LanguageProvider";

/**
 * Pure HTML/CSS recreation of the Centauros "collections" app screen.
 * Fully translatable (no baked-in text), animatable and responsive.
 */
export function CollectionsPanel({ className = "", compact = false }) {
  const { t } = useI18n();
  const a = t("appui");

  const rows = [
    { icon: Zap, title: a.advance, name: "Alejandro", amount: "250,00 €", status: a.pending, tone: "amber" },
    { icon: CreditCard, title: a.extra, name: "Victoria", amount: "50,00 €", status: a.pending, tone: "amber" },
    { icon: Zap, title: a.advance, name: "Victoria", amount: "420,00 €", status: a.paid, tone: "green" },
  ];

  return (
    <div
      className={`w-full overflow-hidden rounded-[1.75rem] border border-black/[0.06] bg-white p-4 shadow-[0_30px_70px_-30px_rgba(0,0,0,0.35)] sm:p-5 ${className}`}
      data-testid="ui-collections-panel"
    >
      {/* header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <span className="flex h-8 w-8 items-center justify-center rounded-full brand-gradient text-[11px] font-bold text-white">OP</span>
          <div className="leading-tight">
            <div className="text-[10px] text-black/40">{a.hello}</div>
            <div className="text-xs font-semibold text-black">Oscar Pérez</div>
          </div>
        </div>
        <span className="h-7 w-7 rounded-full bg-black/[0.05]" />
      </div>

      {/* balance card */}
      <div className="mt-3 rounded-2xl bg-[#0A0A0A] p-4 text-white">
        <div className="text-[10px] uppercase tracking-wide text-white/45">{a.toCollect}</div>
        <div className="mt-1 text-2xl font-semibold tracking-tight sm:text-3xl">420,00 €</div>
        <div className="mt-3 flex gap-5">
          <div>
            <div className="flex items-center gap-1 text-[10px] text-white/45">
              <span className="h-1.5 w-1.5 rounded-full bg-green" /> {a.collected}
            </div>
            <div className="text-xs font-semibold">540,00 €</div>
          </div>
          <div>
            <div className="flex items-center gap-1 text-[10px] text-white/45">
              <span className="h-1.5 w-1.5 rounded-full bg-red-400" /> {a.overdue}
            </div>
            <div className="text-xs font-semibold">0,00 €</div>
          </div>
        </div>
      </div>

      {/* activity */}
      <div className="mt-3 text-[10px] font-medium uppercase tracking-wide text-black/40">{a.activity}</div>
      <div className="mt-1.5 space-y-1.5">
        {(compact ? rows.slice(0, 2) : rows).map((r, i) => {
          const RIcon = r.icon;
          return (
            <div key={i} className="flex items-center gap-3 rounded-xl border border-black/[0.05] px-3 py-2.5">
              <span className={`flex h-7 w-7 items-center justify-center rounded-lg ${r.tone === "green" ? "bg-green/10 text-green" : "bg-amber-400/15 text-amber-500"}`}>
                <RIcon className="h-3.5 w-3.5" strokeWidth={2} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-xs font-semibold text-black">{r.title}</div>
                <div className="truncate text-[10px] text-black/40">{r.name}</div>
              </div>
              <div className="text-right">
                <div className="text-xs font-semibold text-black">{r.amount}</div>
                <div className={`text-[9px] font-semibold ${r.tone === "green" ? "text-green" : "text-amber-500"}`}>{r.status}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Small floating "money in" notification pill, Wise-style. */
export function MoneyPill({ label, amount, className = "", delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 14, scale: 0.92 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ type: "spring", stiffness: 120, damping: 16, delay }}
      className={`inline-flex items-center gap-2.5 rounded-2xl bg-white/95 px-3.5 py-2.5 shadow-[0_18px_40px_-15px_rgba(0,0,0,0.35)] backdrop-blur ${className}`}
    >
      <span className="flex h-8 w-8 items-center justify-center rounded-full brand-gradient text-white">
        <ArrowUpRight className="h-4 w-4" strokeWidth={2.5} />
      </span>
      <div className="leading-tight">
        <div className="text-[10px] font-medium text-black/45">{label}</div>
        <div className="text-sm font-bold text-green">+{amount}</div>
      </div>
    </motion.div>
  );
}

/** Compact invoice UI snippet (for Features bento). */
export function InvoiceSnippet() {
  const { t } = useI18n();
  const a = t("appui");
  return (
    <div className="w-full rounded-2xl border border-black/[0.06] bg-white p-4 shadow-sm">
      <div className="flex items-center gap-2 text-black/70">
        <FileText className="h-4 w-4 text-green" strokeWidth={2} />
        <span className="text-xs font-semibold">#2026-042</span>
        <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-green/10 px-2 py-0.5 text-[10px] font-semibold text-green">{a.paid}</span>
      </div>
      <div className="mt-3 space-y-1.5">
        <div className="h-2 w-3/4 rounded-full bg-black/[0.06]" />
        <div className="h-2 w-1/2 rounded-full bg-black/[0.06]" />
      </div>
      <div className="mt-3 flex items-center justify-between border-t border-black/[0.06] pt-2.5">
        <span className="text-[10px] text-black/40">{a.toCollect}</span>
        <span className="text-sm font-semibold text-black">380,00 €</span>
      </div>
    </div>
  );
}

/** Compact payment link UI snippet (for Features bento). */
export function PayLinkSnippet() {
  const { t } = useI18n();
  const a = t("appui");
  return (
    <div className="w-full rounded-2xl border border-black/[0.06] bg-white p-4 shadow-sm">
      <div className="flex items-center gap-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-full brand-gradient text-white">
          <Wallet className="h-4 w-4" strokeWidth={2} />
        </span>
        <div className="leading-tight">
          <div className="text-xs font-semibold text-black">Victoria M.</div>
          <div className="text-[10px] text-black/40">{a.advance}</div>
        </div>
        <span className="ml-auto text-sm font-bold text-black">49,00 €</span>
      </div>
      <button
        type="button"
        tabIndex={-1}
        className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-xl brand-gradient py-2 text-xs font-semibold text-white"
      >
        <CreditCard className="h-3.5 w-3.5" strokeWidth={2} /> {a.payNow}
      </button>
    </div>
  );
}

const scard = "rounded-2xl border border-black/[0.06] bg-white p-3 shadow-sm";

function Line({ label, value, tone }) {
  const c = tone === "green" ? "text-green" : tone === "amber" ? "text-amber-500" : "text-black/45";
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-[11px] text-black/55">{label}</span>
      <span className={`text-[11px] font-semibold whitespace-nowrap ${tone ? c : "text-black"}`}>{value}</span>
    </div>
  );
}

function PhoneBody({ id, a, pui, methods }) {
  switch (id) {
    case "policies":
      return (
        <div className={scard}>
          {(methods || []).slice(0, 4).map((m, i) => (
            <div key={i} className="flex items-center gap-2.5 border-b border-black/[0.05] py-2 last:border-0">
              <span className={`flex h-4 w-4 items-center justify-center rounded-full ${i === 0 ? "brand-gradient text-white" : "border border-black/15"}`}>
                {i === 0 && <Check className="h-2.5 w-2.5" strokeWidth={3} />}
              </span>
              <span className={`text-[11px] ${i === 0 ? "font-semibold text-black" : "text-black/50"}`}>{m.name}</span>
            </div>
          ))}
        </div>
      );
    case "clients":
      return (
        <div className={scard}>
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-full brand-gradient text-[10px] font-bold text-white">VM</span>
            <div className="leading-tight">
              <div className="text-xs font-semibold text-black">Victoria M.</div>
              <div className="text-[10px] text-black/40">victoria@mail.com</div>
            </div>
          </div>
          <div className="mt-2 divide-y divide-black/5 border-t border-black/5">
            <Line label={a.advance} value="420,00 €" tone="green" />
            <Line label={a.extra} value="45,00 €" tone="amber" />
          </div>
        </div>
      );
    case "payments":
      return <PayLinkSnippet />;
    case "facturacion":
      return <InvoiceSnippet />;
    case "impagos":
      return (
        <div className={scard}>
          <div className="flex items-center gap-2 text-black/70">
            <Bell className="h-3.5 w-3.5 text-amber-500" strokeWidth={2} />
            <span className="text-[11px] font-semibold">{a.overdue}</span>
          </div>
          <div className="mt-2 space-y-1.5">
            <div className="flex items-center justify-between rounded-lg bg-amber-400/10 px-2.5 py-2">
              <span className="text-[11px] text-black/60">Alejandro</span>
              <span className="text-[11px] font-semibold text-amber-500">250,00 €</span>
            </div>
            <div className="flex items-center justify-between rounded-lg bg-green/10 px-2.5 py-2">
              <span className="text-[11px] text-black/60">Victoria</span>
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-green"><Check className="h-3 w-3" strokeWidth={3} />{a.paid}</span>
            </div>
          </div>
        </div>
      );
    case "equipo":
      return (
        <div className={scard}>
          <div className="flex items-center gap-2 text-black/70">
            <Users className="h-3.5 w-3.5 text-green" strokeWidth={2} />
            <span className="text-[11px] font-semibold">Centauros</span>
          </div>
          <div className="mt-2.5 space-y-2">
            {["OP", "VM", "AL"].map((n, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-black/[0.06] text-[9px] font-bold text-black/60">{n}</span>
                <span className="h-1.5 flex-1 rounded-full bg-black/[0.06]" />
                <span className="h-1.5 w-6 rounded-full brand-gradient" />
              </div>
            ))}
          </div>
        </div>
      );
    case "control":
    default:
      return (
        <div className={scard}>
          <div className="text-[10px] uppercase tracking-wide text-black/40">{a.toCollect}</div>
          <div className="text-2xl font-semibold tracking-tight text-black">420,00 €</div>
          <div className="mt-2 flex gap-4 text-[10px]">
            <span className="text-black/45"><span className="mr-1 inline-block h-1.5 w-1.5 rounded-full bg-green align-middle" />{a.collected} 540 €</span>
            <span className="text-black/45"><span className="mr-1 inline-block h-1.5 w-1.5 rounded-full bg-red-400 align-middle" />{a.overdue} 0 €</span>
          </div>
          <div className="mt-2.5 space-y-1.5">
            <div className="flex items-center justify-between gap-2 rounded-lg border border-black/[0.05] px-2.5 py-2">
              <span className="truncate text-[11px] text-black/60">{a.advance}</span>
              <span className="whitespace-nowrap text-[11px] font-semibold text-amber-500">250,00 €</span>
            </div>
            <div className="flex items-center justify-between gap-2 rounded-lg border border-black/[0.05] px-2.5 py-2">
              <span className="truncate text-[11px] text-black/60">{a.advance}</span>
              <span className="whitespace-nowrap text-[11px] font-semibold text-green">420,00 €</span>
            </div>
          </div>
        </div>
      );
  }
}

/** Translatable HTML/CSS recreation of a Centauros app screen (for phone mockups). */
export function PhoneScreen({ id, claim }) {
  const { t } = useI18n();
  return (
    <div className="flex h-full flex-col bg-white">
      <div className="flex items-center justify-between px-5 pt-3 text-[9px] font-semibold text-black/60">
        <span>9:41</span>
        <span className="h-2 w-3.5 rounded-[2px] border border-black/40" />
      </div>
      <div className="mx-auto mb-2 mt-1.5 flex w-2/3 items-center justify-center rounded-full bg-black/[0.05] py-1 text-[9px] font-medium text-black/45">
        centauros.app
      </div>
      <div className="bg-[#D6F5E4] px-4 pb-4 pt-3">
        <h4 className="text-[15px] font-semibold leading-snug tracking-tight text-[#08341f]">{claim}</h4>
      </div>
      <div className="flex-1 overflow-hidden bg-[#F5F8F6] px-3 pt-3">
        <PhoneBody id={id} a={t("appui")} pui={t("policies.ui")} methods={t("policies.methods")} />
      </div>
    </div>
  );
}
